import { useState, useEffect, useMemo, useCallback } from 'react';
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import { Toaster, toast } from 'react-hot-toast';
import { getInventory, searchInventory, createInventory, updateInventory, deleteInventory, clearAllData } from '../utils/api';
import InventoryModal from './InventoryModal';

const InventoryPage = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
  const [clearAllLoading, setClearAllLoading] = useState(false);
  const [clearAllPassword, setClearAllPassword] = useState('');

  // Fetch inventory data
  const fetchInventory = useCallback(async () => {
    try {
      setLoading(true);
      let data;
      if (searchQuery.trim()) {
        data = await searchInventory(searchQuery);
      } else {
        data = await getInventory();
      }
      setInventory(data);
    } catch (error) {
      toast.error(error.message || 'Failed to load inventory');
      console.error('Error fetching inventory:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  // Fetch data on component mount and when search query changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchInventory();
    }, 150); // Reduced debounce for better responsiveness

    return () => clearTimeout(timeoutId);
  }, [searchQuery, fetchInventory]);

  // Define table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'modelName',
        header: 'Model Name',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'modelNumber',
        header: 'Model Number',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'photo',
        header: 'Photo',
        cell: (info) => {
          const photo = info.getValue();
          return photo ? (
            <img
              src={photo}
              alt=""
              className="w-12 h-12 object-cover rounded border shadow-sm"
            />
          ) : (
            <div className="w-12 h-12 border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-gray-50">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          );
        },
      },
      {
        accessorKey: 'size',
        header: 'Category',
        cell: (info) => {
          const size = info.getValue();
          return size ? size.charAt(0).toUpperCase() + size.slice(1) : '-';
        },
      },
      {
        accessorKey: 'retailPrice',
        header: 'Retail Price (₹)',
        cell: (info) => {
          const price = info.getValue();
          return `₹${price.toLocaleString('en-IN')}`;
        },
      },
      {
        accessorKey: 'wholesalePrice',
        header: 'Wholesale Price (₹)',
        cell: (info) => {
          const price = info.getValue();
          return `₹${price.toLocaleString('en-IN')}`;
        },
      },
      {
        accessorKey: 'initialStock',
        header: 'Initial Stock',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'sold',
        header: 'Sold',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'currentStock',
        header: 'Remaining',
        cell: (info) => {
          const stock = info.getValue();
          return stock;
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const item = info.row.original;
          return (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setSelectedItem(item);
                  setIsModalOpen(true);
                }}
                className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors text-sm"
              >
                Edit
              </button>
              <button
                onClick={() => handleDeleteClick(item)}
                disabled={deleteLoading}
                className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                Delete
              </button>
            </div>
          );
        },
      },
    ],
    [deleteLoading]
  );

  // Initialize table
  const table = useReactTable({
    data: inventory,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  // Handle save (create or update)
  const handleSave = async (id, data) => {
    try {
      let result;
      if (id) {
        // Update
        result = await updateInventory(id, data);
        toast.success('Inventory item updated successfully!');
      } else {
        // Create
        result = await createInventory(data);
        toast.success('Inventory item created successfully!');
      }
      // Refresh inventory list
      fetchInventory();
      setIsModalOpen(false);
      setSelectedItem(null);
      // Return the result so modal can access the created item ID for photo upload
      return result;
    } catch (error) {
      toast.error(error.message || 'Failed to save inventory item');
      throw error; // Re-throw to let modal handle it
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  // Handle delete button click
  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  // Handle delete confirmation with optimistic updates
  const handleDeleteConfirm = async () => {
    if (!itemToDelete || deleteLoading) return;

    setDeleteLoading(true);
    const deletedItem = itemToDelete;

    // Optimistic update - remove item from UI immediately
    setInventory(prev => prev.filter(item => item._id !== deletedItem._id));
    setShowDeleteConfirm(false);
    setItemToDelete(null);

    try {
      await deleteInventory(deletedItem._id);
      toast.success('Inventory item deleted successfully!');
    } catch (error) {
      // Revert optimistic update on error
      toast.error(error.message || 'Failed to delete inventory item');
      setInventory(prev => [...prev, deletedItem].sort((a, b) => a.modelName.localeCompare(b.modelName)));
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  // Handle clear all confirmation
  const handleClearAllConfirm = async () => {
    if (clearAllLoading || !clearAllPassword.trim()) return;

    setClearAllLoading(true);
    setShowClearAllConfirm(false);

    try {
      await clearAllData(clearAllPassword);
      toast.success('All data has been cleared successfully!');
      // Reset password and refresh inventory list
      setClearAllPassword('');
      fetchInventory();
    } catch (error) {
      toast.error(error.message || 'Failed to clear all data');
    } finally {
      setClearAllLoading(false);
    }
  };

  // Handle clear all cancel
  const handleClearAllCancel = () => {
    setShowClearAllConfirm(false);
  };

  return (
    <div className="p-6">
      <Toaster position="top-right" />

      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-gray-800 mb-2">
          Inventory Management
        </h1>
        <p className="text-gray-600">Manage your Ganesh idol inventory</p>
      </div>

      {/* Search and Action Buttons */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by model name or number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex gap-2 flex-col sm:flex-row">
            <button
              onClick={() => setShowClearAllConfirm(true)}
              disabled={clearAllLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold whitespace-nowrap"
            >
              🗑️ Clear All Data
            </button>
            <button
              onClick={() => {
                setSelectedItem(null);
                setIsModalOpen(true);
              }}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold whitespace-nowrap"
            >
              + Add New Model
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-600">Loading...</div>
        ) : inventory.length === 0 ? (
          <div className="p-8 text-center text-gray-600">
            {searchQuery ? 'No items found matching your search.' : 'No inventory items found.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() && (
                            <span>
                              {header.column.getIsSorted() === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {table.getRowModel().rows.map((row) => {
                  const item = row.original;
                  const isLowStock = item.currentStock < 5;
                  return (
                    <tr
                      key={row.id}
                      className={isLowStock ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={`px-6 py-4 whitespace-nowrap text-sm ${
                            isLowStock ? 'text-red-800 font-semibold' : 'text-gray-900'
                          }`}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Low Stock Warning */}
      {inventory.some((item) => item.currentStock < 5) && (
        <div className="mt-4 p-4 bg-red-100 border border-red-300 rounded-lg text-red-800">
          <p className="font-semibold">⚠ Low Stock Alert</p>
          <p className="text-sm">
            Items with remaining stock less than 5 are highlighted in red.
          </p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Inventory Item</h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete <strong>"{itemToDelete.modelName}"</strong> (Model: {itemToDelete.modelNumber})?
                This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={handleDeleteCancel}
                  disabled={deleteLoading}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleteLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {deleteLoading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clear All Confirmation Modal */}
      {showClearAllConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Clear All Data</h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete <strong>ALL data</strong> from the database? This includes:
              </p>
              <ul className="text-sm text-gray-600 mb-4 text-left bg-red-50 p-3 rounded">
                <li>• All inventory items</li>
                <li>• All orders and order history</li>
                <li>• All counters and sequences</li>
              </ul>

              {/* Password Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter Password to Confirm
                </label>
                <input
                  type="password"
                  value={clearAllPassword}
                  onChange={(e) => setClearAllPassword(e.target.value)}
                  placeholder="Enter confirmation password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  disabled={clearAllLoading}
                />
              </div>

              <p className="text-sm text-red-600 font-medium mb-4">
                ⚠️ This action cannot be undone!
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={handleClearAllCancel}
                  disabled={clearAllLoading}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearAllConfirm}
                  disabled={clearAllLoading || !clearAllPassword.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {clearAllLoading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  {clearAllLoading ? 'Clearing...' : 'Clear All Data'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      <InventoryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        item={selectedItem}
      />
    </div>
  );
};

export default InventoryPage;
